import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/api';
import defaultAvatar from '../../assets/img/team-1.jpg';

interface HeaderProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    mobileShow: boolean;
    setMobileShow: (show: boolean) => void;
    theme: 'light' | 'dark';
    toggleTheme: () => void;
}

const Header: React.FC<HeaderProps> = ({ collapsed, setCollapsed, mobileShow, setMobileShow, theme, toggleTheme }) => {
    const { user, logout } = useAuth();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <header className="sticky top-0 z-40 h-20 flex items-center justify-between px-6 md:px-10 border-b border-white/[0.05] shrink-0 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>
            <div className="flex items-center gap-6">
                <button
                    className="hidden lg:flex items-center justify-center w-12 h-12 rounded-2xl bg-white/5 text-white hover:bg-[#ffbb00] hover:text-[#05144b] transition-all transform active:scale-90 shadow-sm border border-white/5"
                    onClick={() => setCollapsed(!collapsed)}
                    title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    <i className={`fa-solid ${collapsed ? 'fa-indent' : 'fa-outdent'} text-lg`}></i>
                </button>
                <button
                    className="lg:hidden flex items-center justify-center w-12 h-12 rounded-2xl bg-indigo-600 text-white transition-all shadow-lg shadow-indigo-600/20 active:scale-90"
                    onClick={() => setMobileShow(!mobileShow)}
                >
                    <i className="fa-solid fa-bars-staggered text-lg"></i>
                </button>
                <div className="hidden md:block">
                    <div className="flex flex-col">
                         <h1 className="text-[20px] font-black text-white flex items-center gap-3 uppercase tracking-tight">
                            <span className="w-1.5 h-6 bg-[#ffbb00] rounded-full shadow-[0_0_15px_rgba(255,187,0,0.4)]"></span>
                            {(() => {
                                const path = window.location.pathname;
                                const items: {[key: string]: string} = {
                                    '/admin-dashboard': 'Operational Overview',
                                    '/admin-users': 'User Management',
                                    '/admin-sub-admins': 'Sub-Admin Management',
                                    '/admin-site-visibility': 'Site Visibility',
                                    '/admin-lead-settings': 'Lead Controls',
                                    '/admin-seo': 'SEO Strategy',
                                    '/admin-packages': 'Business Packages',
                                    '/admin-package-inquiries': 'Package Inquiries',
                                    '/admin-inventory': 'Inventory Manager',
                                    '/admin-developers': 'Developer Network',
                                    '/admin-bookings': 'Site Visit Bookings',
                                    '/admin-leads': 'CRM Leads',
                                    '/admin-add-property': 'Bulk Property Import',
                                    '/admin-saved-properties': 'Property Vault',
                                    '/admin-lead-classified-packages': 'Classified Packages',
                                    '/admin-lead-subscriptions': 'Subscription Registry',
                                    '/admin-cities': 'City Directory',
                                    '/admin-localities': 'Locality Database',
                                    '/admin-locality-highlights': 'Locality Highlights',
                                    '/admin-landmark-categories': 'Landmark Categories',
                                    '/admin-price-trends': 'Market Analytics',
                                    '/admin-amenities': 'Amenity Library',
                                    '/admin-specifications': 'Technical Specs',
                                    '/admin-podcasts': 'Podcast Media',
                                    '/admin-reels': 'Reel Management',
                                    '/admin-testimonials': 'Client Reviews',
                                    '/admin-blogs': 'Content Hub',
                                    '/admin-blog-comments': 'Engagement Moderation',
                                    '/admin-hero-banners': 'Hero Banners',
                                    '/admin-profile': 'Account Settings',
                                    '/admin-change-password': 'Security Vault',
                                    '/admin-projects': 'All Projects',
                                    '/admin-drafts': 'Project Drafts',
                                    '/admin-add-project': 'New Project Entry',
                                    '/admin-project-inquiries': 'Project Inquiries',
                                    '/admin-locality-inquiries': 'Locality Inquiries',
                                    '/admin-common-inquiries': 'Common Portal Inquiries'
                                };
                                return items[path] || 'Management Console';
                            })()}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4 md:gap-8">
                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className="w-12 h-12 rounded-2xl bg-white/5 text-white hover:bg-[#ffbb00] hover:text-[#05144b] transition-all flex items-center justify-center shadow-sm border border-white/5 group overflow-hidden relative"
                    title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                >
                    <div className="relative z-10">
                        <i className={`fa-solid ${theme === 'light' ? 'fa-moon' : 'fa-sun'} text-lg transition-transform duration-500 group-hover:rotate-[360deg]`}></i>
                    </div>
                    <div className="absolute inset-0 bg-[#ffbb00] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>

                {/* User Dropdown */}
                <div className="relative" ref={dropdownRef}>
                    <div 
                        className="flex items-center gap-4 p-1.5 pr-4 rounded-[1.8rem] bg-white/5 hover:bg-white/10 transition-all cursor-pointer border border-white/5 shadow-sm group active:scale-[0.98]"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        <div className="relative">
                            <img
                                src={user?.avatar ? getImageUrl(user.avatar) : defaultAvatar}
                                alt="Admin"
                                className="w-10 h-10 md:w-11 md:h-11 rounded-[1.2rem] object-cover ring-4 ring-[#05144b] shadow-xl group-hover:scale-105 transition-all"
                                onError={(e: any) => { e.target.src = defaultAvatar; }}
                            />
                            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 border-2 border-[#05144b] rounded-full shadow-sm"></div>
                        </div>
                         <div className="hidden sm:block">
                             <h6 className="text-sm font-bold text-white leading-tight tracking-tight">{user?.name || 'Admin'}</h6>
                            <div className="flex items-center gap-1.5 mt-1">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                 <p className="text-[11px] font-semibold text-blue-100/70 leading-none">Operational</p>
                            </div>
                        </div>
                        <i className={`fa-solid fa-chevron-down text-[10px] text-white transition-transform duration-500 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                    </div>

                    {dropdownOpen && (
                        <div className="absolute right-0 mt-3 w-64 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-[101]" style={{ background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)' }}>
                            <div className="px-5 py-3.5 border-b border-white/5 bg-white/5">
                                 <div className="font-bold text-white text-base mb-0.5">{user?.name || 'Admin'}</div>
                                 <div className="text-blue-100/60 text-xs font-medium truncate">{user?.email}</div>
                            </div>
                            
                            <div className="p-2 space-y-0.5">
                                <Link 
                                    to="/admin-profile" 
                                    className="flex items-center gap-3 px-4 py-2 text-blue-100/80 hover:bg-white/10 hover:text-white rounded-xl transition-all font-semibold text-base group"
                                    onClick={() => setDropdownOpen(false)}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-all">
                                        <i className="fa-solid fa-user-gear text-base transition-transform group-hover:scale-110"></i>
                                    </div>
                                     Edit Profile
                                </Link>
                                <Link 
                                    to="/admin-change-password" 
                                    className="flex items-center gap-3 px-4 py-2 text-blue-100/80 hover:bg-white/10 hover:text-white rounded-xl transition-all font-semibold text-base group"
                                    onClick={() => setDropdownOpen(false)}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-all">
                                        <i className="fa-solid fa-shield-halved text-base transition-transform group-hover:scale-110"></i>
                                    </div>
                                    Privacy
                                </Link>
                            </div>

                            <div className="p-2 border-t border-white/5 bg-white/[0.02]">
                                <button 
                                    className="flex items-center gap-3 w-full px-4 py-2 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 rounded-xl transition-all font-semibold text-base group"
                                    onClick={() => { logout(); setDropdownOpen(false); }}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-all">
                                        <i className="fa-solid fa-power-off text-base transition-transform group-hover:rotate-12"></i>
                                    </div>
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
